import * as d3 from "d3";
import React from "react";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";

export interface IGroupedData {
  label: string;
  values: number[];
}
export interface ILineData {
  index: number;
  value: number;
}
interface Props {
  data: IGroupedData[];
  PercentageSign: string;
  legendsHOB: string[];
  colorsForBars: string[];
  lineData: [number, number][];
}

interface BarProps {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  onMouseEnter: (e: MouseEvent<SVGPathElement>) => void;
  onMouseLeave: () => void;
}

interface Tooltip {
  x: number;
  y: number;
  index: number;
}

function Bar({
  x,
  y,
  width,
  height,
  color,
  onMouseEnter,
  onMouseLeave,
}: BarProps) {
  const radius = height === 0 ? 0 : 3.0;
  return (
    <path
      d={`
      m${x},${y + radius}
      a${radius},${radius} 0 0 1 ${radius},${-radius}
      h${width - 2 * radius}
      a${radius},${radius} 0 0 1 ${radius},${radius}
      v${height - radius}
      h-${width}
      z
    `}
      fill={color}
      onMouseEnter={(event) => onMouseEnter(event)}
      onMouseLeave={onMouseLeave}
    />
  );
}
export function BarCharWithLineVolumeNDDashboard({
  data,
  PercentageSign,
  legendsHOB,
  colorsForBars,
  lineData,
}: Props) {
  //  useEffect(() => {}, []);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const axisBottomRef = useRef<SVGGElement>(null);
  const axisLeftRef = useRef<SVGGElement>(null);

  var BottomDivWidth: any =
    document.getElementById("barchartdivFinanceHOB")?.clientWidth || 715;

  const margin = { top: 12, right: 0, bottom: 220, left: 55 };
  let width = BottomDivWidth - 15 - margin.left - margin.right || 0;
  const height = 410 - margin.top - margin.bottom;

  const labels = data.map(({ label }) => label);
  var sublabels: any = [];
  if (data[0]) sublabels = Object.keys(data[0].values);
  const scaleX = d3.scaleBand().domain(labels).range([0, width]).padding(0.2);
  var maxYscale = 0;
  const [refresh, setrefresh] = useState(false);
  data.map((d) => {
    var localMax = 0;
    d.values.map((v) => {
      localMax = v;

      if (localMax > maxYscale) maxYscale = localMax;
    });
  });
  lineData.map((d) => {
    var localMax = d[1];

    if (localMax > maxYscale) maxYscale = localMax;
  });

  maxYscale = maxYscale + 10;
  const scaleY = d3.scaleLinear().domain([0, maxYscale]).range([height, 0]);

  const subscaleX = d3
    .scaleBand()
    .domain(sublabels)
    .range([0, scaleX.bandwidth()])
    .padding(0.1);

  useEffect(() => {
    var BottomDivWidth: any =
      document.getElementById("barchartdivFinanceHOB")?.clientWidth || 715;

    width = BottomDivWidth - 15 - margin.left - margin.right || 0;

    setrefresh(!refresh);
  }, [width]);

  useEffect(() => {
    if (axisBottomRef.current) {
      d3.select(axisBottomRef.current)
        .call(d3.axisBottom(scaleX))
        .selectAll("text")
        .style("text-anchor", "end")
        .attr("dx", "-.8em")
        .attr("dy", ".15em")
        .attr("transform", "rotate(-65)")
        .text(function (d: any) {
          return d;
          // return d.length > 20 ? d.substring(0, 18) + "..." : d;
        });
    }

    if (axisLeftRef.current) {
      d3.select(axisLeftRef.current).call(
        d3
          .axisLeft(scaleY)
          .tickValues(
            d3.range(0, maxYscale + 10, Math.round(maxYscale / 10) * 10)
          )
          .tickFormat(function (d, i) {
            return PercentageSign + d?.toLocaleString();
          })
      );
    }
  }, [scaleX, scaleY]);

  var z = d3.scaleOrdinal(colorsForBars);
  //Initialize legend
  var legendItemSize = 12;
  var legendSpacing = 4;
  var xOffset = 150;
  var yOffset = 100;
  d3.select(".Legend2").select("svg").remove();

  const linePath =
    d3
      .line()
      .x(
        (d) =>
          (scaleX(labels[d[0] - 1]) || 0) + margin.left + scaleX.bandwidth() / 2
      )
      .y((d) => scaleY(d[1]) + margin.top)(lineData) || undefined;
  var legend = d3
    .select(".Legend2")
    .append("svg")
    .selectAll(".legendItem")
    .data(legendsHOB);

  //Create legend items
  legend
    .enter()
    .append("rect")
    .attr("class", "legendItem")
    .attr("width", legendItemSize)
    .attr("height", legendItemSize)

    .style("fill", function (i) {
      return z(i);
    })
    .attr("transform", (d, i) => {
      var x = xOffset;
      var y = yOffset + (legendItemSize + legendSpacing) * i;
      return `translate(${x}, ${y})`;
    });

  //Create legend labels
  legend
    .enter()
    .append("text")
    .attr("x", xOffset + legendItemSize + 5)
    .attr("y", (d, i) => yOffset + (legendItemSize + legendSpacing) * i + 12)
    .text((d: any) => {
      return d;
    });

  return (
    <div className="" id="barchartdivFinanceHOB">
      <svg
        width={width + margin.left + margin.right}
        height={height + margin.top + margin.bottom}
      >
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <g ref={axisBottomRef} transform={`translate(0, ${height})`} />
          <g ref={axisLeftRef} />
          {data.map(({ label, values }, groupIndex) => (
            <g
              key={`rect-group-${groupIndex}`}
              transform={`translate(${scaleX(label)}, 0)`}
            >
              {values.map((value, barIndex) => (
                <>
                  <Bar
                    key={`rect-${barIndex}`}
                    x={subscaleX(String(barIndex)) || 0}
                    y={scaleY(value)}
                    width={subscaleX.bandwidth()}
                    height={height - scaleY(value)}
                    color={colorsForBars[barIndex]}
                    onMouseEnter={(event) => {
                      setTooltip({
                        x: event.clientX,
                        y: event.clientY,
                        index: groupIndex,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  />
                  {lineData && lineData.length > 0 && (
                    <circle
                      fill={colorsForBars[1]}
                      stroke={"none"}
                      cx={
                        (subscaleX(String(1)) || 0) +
                        subscaleX.bandwidth() / 2 +
                        7
                      }
                      //                cx={ ((getX(labels[(lineData[groupIndex][0]-1)])|| 0) ||0 - ( groupIndex+1 * subscaleX.bandwidth() * scaleX.bandwidth()))}
                      cy={scaleY(lineData[groupIndex][1])}
                      r={3}
                      onMouseEnter={(event) => {
                        setTooltip({
                          x: event.clientX,
                          y: event.clientY,
                          index: groupIndex,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  )}{" "}
                </>
              ))}
            </g>
          ))}
        </g>
        <path
          className="c-linechart-line__path"
          d={linePath}
          strokeLinecap="round"
          strokeWidth="3"
          stroke={colorsForBars[1]}
          fill="none"
        />
      </svg>

      {tooltip !== null ? (
        <div className="tooltip" style={{ top: tooltip.y, left: tooltip.x }}>
          <span className="tooltip__title">{labels[tooltip.index]}</span>
          <table className="tooltip__table">
            <tbody>
              <tr key={"rowModel1111"}>
                <td>{legendsHOB[0]}</td>
                <td>
                  {PercentageSign}
                  {data[tooltip.index].values[0]?.toLocaleString()}
                </td>
              </tr>
              <tr key={"rowModel1111"}>
                <td>{legendsHOB[1]}</td>
                {lineData && lineData.length > 0 && (
                  <td>
                    {PercentageSign}
                    {lineData[tooltip.index][1]?.toLocaleString()}
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
